import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../icons/icons.tsx';
import { cx } from '../lib/cx.ts';
import { IconButton } from '../primitives/Button.tsx';
import { useUiT } from '../i18n/index.tsx';

export type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';
const TOAST_ICON: Record<ToastTone, string> = {
  success: 'check',
  error: 'warning',
  warning: 'warning',
  info: 'info',
  neutral: 'bell',
};

export interface ToastViewProps {
  tone?: ToastTone;
  icon?: string;
  title: ReactNode;
  sub?: ReactNode;
  action?: ReactNode;
  onClose?: () => void;
}

/** The toast card (`.md-toast`). */
export function Toast({ tone = 'neutral', icon, title, sub, action, onClose }: ToastViewProps) {
  const t = useUiT();
  return (
    <div className={cx('md-toast', 'md-toast--' + tone)} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="md-toast-icon">
        <Icon name={icon || TOAST_ICON[tone]} size={16} stroke={2} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div>{title}</div>
        {sub ? <div className="md-toast-sub">{sub}</div> : null}
      </div>
      {action}
      <IconButton icon="close" label={t('common.close')} size="sm" onClick={onClose} />
    </div>
  );
}

export interface ToastOptions {
  tone?: ToastTone;
  title: ReactNode;
  sub?: ReactNode;
  icon?: string;
  /** action button: label + handler */
  action?: { label: string; onClick: () => void };
  /** ms; default 4000; errors persist until dismissed unless set */
  duration?: number;
  /** de-duplicate: replaces a visible toast with the same id */
  id?: string;
}
interface ToastItem extends ToastOptions {
  key: string;
}

interface ToastApi {
  toast: (opts: ToastOptions) => string;
  dismiss: (key: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((key: string) => {
    setItems(list => list.filter(t => t.key !== key));
    const t = timers.current.get(key);
    if (t) clearTimeout(t);
    timers.current.delete(key);
  }, []);

  const toast = useCallback(
    (opts: ToastOptions) => {
      const key = opts.id ?? Math.random().toString(36).slice(2);
      setItems(list => [...list.filter(t => t.key !== key), { ...opts, key }].slice(-MAX_VISIBLE));
      const duration = opts.duration ?? (opts.tone === 'error' ? 0 : 4000);
      const prev = timers.current.get(key);
      if (prev) clearTimeout(prev);
      if (duration > 0) timers.current.set(key, setTimeout(() => dismiss(key), duration));
      return key;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach(t => clearTimeout(t));
  }, []);

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="md-toast-viewport" aria-live="polite" aria-relevant="additions">
        {items.map(t => (
          <Toast
            key={t.key}
            tone={t.tone}
            icon={t.icon}
            title={t.title}
            sub={t.sub}
            onClose={() => dismiss(t.key)}
            action={
              t.action ? (
                <button
                  type="button"
                  className="md-btn md-btn--sm md-btn--secondary"
                  onClick={() => {
                    t.action?.onClick();
                    dismiss(t.key);
                  }}
                >
                  {t.action.label}
                </button>
              ) : undefined
            }
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
